import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[800px] mx-auto px-4 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-lg font-heading font-bold text-foreground-900 mb-2">
              漫画不存在
            </h1>
            <p className="text-sm text-foreground-500 mb-4">
              抱歉，您要查看的漫画可能已下架或不存在
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.categorySlug === comic.categorySlug || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const categoryName =
    comic.categorySlug === 'hanman'
      ? '韩漫'
      : comic.categorySlug === 'riman'
        ? '日漫'
        : comic.categorySlug === 'guoman'
          ? '国漫'
          : comic.category;

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} - 歪歪漫画在线免费观看`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.chapters[0]?.updatedAt,
          dateModified: comic.updatedAt,
          url: `https://www.waiwaimanhua.com/comic/${comic.id}`,
          image: comic.coverImage,
          publisher: {
            '@type': 'Organization',
            name: '歪歪漫画网',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.waiwaimanhua.com' },
            { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.waiwaimanhua.com/category/${comic.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: comic.title },
          ],
        }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-72 shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 歪歪漫画在线免费阅读`}
                  title={`${comic.title} - 歪歪漫画在线免费观看全集`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">作者：{comic.author}</p>

              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                  <i className="ri-star-fill"></i> {comic.rating} 分
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                  <i className="ri-eye-line"></i>{' '}
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 次观看
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                  <i className="ri-heart-line"></i>{' '}
                  {comic.favorites >= 10000 ? `${(comic.favorites / 10000).toFixed(0)}万` : comic.favorites} 收藏
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}
                >
                  <i className={comic.status === '连载中' ? 'ri-loader-4-line' : 'ri-check-double-line'}></i>
                  {comic.status}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs rounded-md bg-background-100 text-foreground-600 border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 p-4 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2">
                  作品简介
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <p className="text-xs text-foreground-400 mt-3">
                最后更新：{comic.updatedAt}
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-10">
          <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
            章节列表
            <span className="text-sm font-normal text-foreground-500 ml-2">
              共 {comic.chapters.length} 话
            </span>
          </h2>
          <div className="border border-background-200/70 rounded-lg overflow-hidden">
            <div className="divide-y divide-background-200/70">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                      {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    {chapter.updatedAt}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-14 mb-14">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}